
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import List from '@/common/components/UI/List/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'

// module components
import FeedbacksListItem from '@/modules/VacanciesFeedbacks/components/FeedbacksListItem/index.vue'
import FeedbacksActions from '@/modules/VacanciesFeedbacks/components/FeedbacksActions/index.vue'

// realisations
import { vacancies } from '@/core/realisations/vacancies.realisation'

// types
import { FeedbackStatusT, VacanciesFeedbackT } from '@/core/types/vacancies.types'

// composable
import usePagination from '@/common/composable/usePagination'
import useCatchError from '@/common/composable/useCatchError'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        list: List,
        'failed-fetch': FailedFetch,
        'feedbacks-list-item': FeedbacksListItem,
        'feedbacks-actions': FeedbacksActions,
    },
    setup() {
        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_change_status_mode: false,
            is_change_status_loading: false,
            initial_value: null as { status_id: number; feedback_index: number } | null,
            feedbacks: [] as VacanciesFeedbackT[],
        })

        const { error_state, catchError } = useCatchError()
        const { pagination_state } = usePagination()

        const loadFeedbacks = async (page: number) => {
            pagination_state.current_page = page

            try {
                state.is_loading = true
                const data = await vacancies.getFeedBacksList(page)

                state.feedbacks = data.data
                pagination_state.total_pages = data.meta.last_page
                pagination_state.total_items = data.meta.total

                state.is_failed = false
                error_state.is_cancel = false
            } catch (error) {
                catchError(error)

                if (!error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadFeedbacks(1)
        })

        const startChangeStatus = ({
            status_id,
            feedback_index,
        }: {
            status_id: number
            feedback_index: number
        }) => {
            state.initial_value = { status_id, feedback_index }
            state.is_change_status_mode = true
        }

        const cancelChangeStatus = () => {
            state.initial_value = null
            state.is_change_status_mode = false
        }

        const changeStatus = async ({
            status,
            feedback_index,
        }: {
            status: FeedbackStatusT
            feedback_index: number
        }) => {
            try {
                state.is_change_status_loading = true

                await vacancies.setFeedbackStatus(state.feedbacks[feedback_index].id, status.id)

                state.feedbacks[feedback_index].status_id = status.id
                state.feedbacks[feedback_index].status_name = status.name
                state.feedbacks[feedback_index].status_code = status.code

                state.is_change_status_mode = false
            } catch (error) {
                console.error(error)
            } finally {
                state.is_change_status_loading = false
            }
        }

        const onArchive = (feedback_index: number) => {
            state.feedbacks[feedback_index].is_archive = true
        }

        const onUnarchive = (feedback_index: number) => {
            state.feedbacks[feedback_index].is_archive = false
        }

        return {
            state,
            error_state,
            loadFeedbacks,
            pagination_state,
            startChangeStatus,
            cancelChangeStatus,
            changeStatus,
            onArchive,
            onUnarchive,
        }
    },
})
